import { HttpError } from '@magicdoorinc/errors';
import { createTriggerResource, createMutation } from '@magicdoorinc/ui';
import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { UnitListingsRepository } from '~/repositories/unitListingsRepository';
import type { UnitListingFilter, addUnitListingPayload } from '~/repositories/unitListingsRepository';
import type {
  AddUnitListingImageItemDto,
  UnitListingApplicationInviteDto,
  UnitListingSyndicationStatusDto,
  UnitMasterListingRequestDto,
  UpdateUnitListingDto,
  UpdateUnitListingImageItemDto,
  UpdateUnitListingImagesDto,
} from '~/swagger/portal/Api';

const repo = new UnitListingsRepository();

export const [UnitListingsProvider, useUnitListings] = createMagicDoorContext('UnitListings', () => {
  const [unitListings, getUnitListings, { refetch }] = createTriggerResource((filter: UnitListingFilter) => repo.getUnitListings(filter));
  const [unitListingDetail, getUnitListingsDetail, { refetch: refetchUnitListingDetail, mutate: mutateUnitListingDetail }] =
    createTriggerResource((unitListingId: string) => repo.getUnitListingDetail(unitListingId));
  const [unitListingSyndicationStatus, getUnitListingSyndicationStatus, syndicationStatusActions] = createTriggerResource(
    (unitListingId: string) => repo.getUnitListingSyndicationStatus(unitListingId)
  );
  const [syndicationError, setSyndicationError] = createSignal<unknown>(null);

  const getUnitListingSyndicationStatusWithErrorHandling = async (
    unitListingId: string
  ): Promise<UnitListingSyndicationStatusDto | undefined> => {
    try {
      setSyndicationError(null);
      return await repo.getUnitListingSyndicationStatus(unitListingId);
    } catch (error) {
      setSyndicationError(error);
      if (error instanceof HttpError && error.getStatusCode() === 409) {
        return undefined;
      }
      console.error(error);
      throw error;
    }
  };

  const deleteUnitListingImage = createMutation(async (id: string, imageId: string) => {
    await repo.deleteUnitListingImage(id, imageId);
  });
  const uploadUnitListingImages = createMutation(async (unitId: string, files: File[]) => {
    return await repo.uploadUnitListingImages(unitId, files);
  });
  const addUnitListingImages = createMutation(async (unitId: string, items: AddUnitListingImageItemDto[]) => {
    await repo.addUnitListingImages(unitId, items);
  });
  const addUnitListing = createMutation(async (payload: addUnitListingPayload) => {
    const response = await repo.addUnitListing(payload);
    refetch();
    return response;
  });
  const generateAiListing = createMutation(async (unitId: string, prompt: UnitMasterListingRequestDto) => {
    return await repo.generateAiListing(unitId, prompt);
  });
  const updateUnitListing = createMutation(async (model: UpdateUnitListingDto, unitId: string) => {
    const response = await repo.updateUnitListing(model, unitId);
    refetchUnitListingDetail();
    getUnitListingSyndicationStatusWithErrorHandling(unitId).catch(() => {});
    return response;
  });
  const updateUnitListingImages = createMutation(async (unitId: string, model: UpdateUnitListingImagesDto) => {
    const response = await repo.updateUnitListingImages(unitId, model);
    return response;
  });
  const updateUnitListingImageItems = createMutation(async (unitId: string, items: UpdateUnitListingImageItemDto[]) => {
    await repo.updateUnitListingImages(unitId, items);
  });
  const removeUnitListingImage = createMutation(async (unitId: string, imageId: string) => {
    await repo.removeUnitListingImage(unitId, imageId);
  });
  const deactivateUnitListing = createMutation(async (unitId: string) => {
    const response = await repo.deactivateUnitListing(unitId);
    return response;
  });

  const applicationInvite = createMutation(async (unitId: string, payload: UnitListingApplicationInviteDto) => {
    const response = await repo.applicationInvite(unitId, payload);
    return response;
  });

  return {
    unitListings,
    getUnitListings,
    unitListingDetail,
    getUnitListingsDetail,
    refetchUnitListingDetail,
    deleteUnitListingImage,
    uploadUnitListingImages,
    addUnitListingImages,
    addUnitListing,
    generateAiListing,
    updateUnitListing,
    updateUnitListingImages,
    updateUnitListingImageItems,
    removeUnitListingImage,
    deactivateUnitListing,
    unitListingSyndicationStatus,
    getUnitListingSyndicationStatus,
    getUnitListingSyndicationStatusWithErrorHandling,
    syndicationStatusActions,
    syndicationError,
    applicationInvite,
    refetchUnitListing: refetch,
    mutateUnitListingDetail,
  };
});
